/**
 * Pairing route (MyApp bridge / reconciliation engine).
 *
 * Pick a binding (a "pairing" instance), then pair Dimension-A records with
 * Dimension-B records via the editable matrix, reconciling each side against its
 * target. Seeded bindings live in BRIDGE_BINDINGS below; builder-authored ones
 * are published bridge-type tables carrying their config in BridgeBindingJson.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";

import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { SearchCombobox } from "@/components/ui/search-combobox";
import { AllocationMatrix } from "@/components/portal/myapp/AllocationMatrix";
import { ContainerPackingView } from "@/components/portal/myapp/ContainerPackingView";
import { ReceivingView } from "@/components/portal/myapp/ReceivingView";
import { BindingBuilder } from "@/components/portal/myapp/BindingBuilder";
import { BindingSummaryCard } from "@/components/portal/myapp/BindingSummaryCard";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useMyAppBatchPermissions } from "@/hooks/useMyAppBatchPermissions";
import { myappApi } from "@/services/api/myapp";
import type { FormDetail } from "@/types/myapp";
import { bridgeTableToConfig, type BridgeDemoConfig } from "@/components/portal/myapp/bridgeConfig";

export const Route = createFileRoute("/portal/myapp/bridge")({
  component: AllocationsPage,
});

// ---------------------------------------------------------------------------
// SEEDED bindings — the BridgeDemoConfig type lives in bridgeConfig.ts.
// All ids are MyApp TableDef / TableField ids (UUID strings); the *Pk / label
// columns are physical column names on those tables.
// ---------------------------------------------------------------------------

// Demo Free Range · D05 container-allocation instance (seeded binding).
const D05_BINDING: BridgeDemoConfig = {
  id: "d05-container-demo",
  label: "Container Allocation — D05 demo (Demo Free Range)",
  description: "Allocate purchase-order lines into shipping containers by volume.",
  rowParentLabel: "Purchase order",
  bridgeTableId: "37AB0A19-C30F-452D-A1ED-F3E57F2473CA", // D05 Allocation
  binding: {
    name: "D05 Container Allocation",
    measure_field_id: "500347EA-AFEE-47B6-9614-5C8830C35A1F", // Allocation.Volume Input
    units_field_id: "C1DB07F9-336D-4A0D-AAD3-190CAD3F5444", // Allocation.Units (entered units)
    dim_a: {
      // PO Line side
      link_field_id: "8E14194D-D32A-4CF4-8D80-E09D4912905F", // Allocation.PO Line link
      linked_table_id: "35CDA3BB-CD15-427E-A0EA-F5512BDEE4E6", // D05 Purchase Orders Lines
      target_field_id: "2663366A-307F-4E01-A47D-6FC799B54813", // Line Volume (= Qty × Volume)
      label_field_id: "ADD783FD-DF08-4B18-97E3-ACFC3D8ACD66", // SKU (row label)
      group_field_id: "CA03627E-26D8-4744-A158-D4C4BBD75846", // OrderNumber (PO context)
      conversion_field_id: "8BFC8DAE-2129-44CC-A8C0-AE495C9AFA7E", // line Volume (unit-volume factor)
      op: "LE",
      enforcement: "soft",
      // Parent (header) block: required by the composite read (BridgeService.
      // read_composite raises without it) and switches the PO picker to the
      // worklist (parents mode) below. No gate fields — D05 headers stay
      // always-editable, matching current demo behaviour.
      parent: {
        table_id: "C9D72F2F-065C-4024-836C-ADB196BFE837", // D05 Purchase Orders (= poHeaderTableId)
        link_field_id: "384703C1-A6C0-4BB4-AB6D-33617A232A50", // D05 Purchase Orders link (on the lines table)
        label_field_id: "9B974C54-4CE9-4959-AD7C-7587850FCB84", // OrderNumber
      },
    },
    dim_b: {
      // Container side
      link_field_id: "662577B3-F650-40F2-ABF0-B9B2B3BC7F66", // Allocation.Container Code link
      linked_table_id: "A8A3E70B-2258-4F68-A675-B3C34170F352", // D05 Containers
      target_field_id: "537C1964-04FE-4327-8BD1-3C2EA635B370", // Container Volume (capacity)
      label_field_id: "D13F0CA5-FED7-4F79-91B6-08C8667A3D84", // Container Code (column label)
      gate_field_id: "3E9E2691-1FDB-47C0-AE7E-8DF9B723F6FE", // Container Status link
      gate_editable_values: ["Filling"],
      op: "LE",
      enforcement: "soft",
    },
  },
  poHeaderTableId: "C9D72F2F-065C-4024-836C-ADB196BFE837", // D05 Purchase Orders
  poHeaderPkColumn: "D05PurchaseOrdersId",
  poHeaderLabelColumn: "OrderNumber",
  poLinesTableId: "35CDA3BB-CD15-427E-A0EA-F5512BDEE4E6",
  poLinesPkColumn: "D05PurchaseOrdersLinesId",
  poLinesUnitVolumeColumn: "Volume", // line unit-volume (materialised from Product.Volume)
  poLinesLabelColumn: "SKU",
  poLinesGroupColumn: "OrderNumber", // PO context shown as the row sub-label

  containerTableId: "A8A3E70B-2258-4F68-A675-B3C34170F352", // D05 Containers
  containerPkColumn: "D05ContainersId",
  containerLabelColumn: "ContainerCode",
  containerStatusColumn: "ContainerStatus",
  containerStatusEditableValue: "Filling",
};

// All seeded reconciliation bindings (Track A.5). Append Supernova + other-client
// entries here as their tables are published — each is one instance the picker
// switches between. Track B replaces this constant with a server-backed list
// (typed BridgeBinding table); the binding shape is identical, so the picker +
// matrix + builder all carry over unchanged.
const BRIDGE_BINDINGS: BridgeDemoConfig[] = [D05_BINDING];

function AllocationsPage() {
  const { token } = useAuth();
  // Pairing is mainstreamed into the standard MyApp permission model (2026-07):
  // organisation owners/admins (and platform admins) — everyone CP reports as
  // `bypassesGroups` — can both USE and AUTHOR pairings with no extra grant,
  // matching the BE (tables.py gates bridge create / edit-binding on org-admin).
  // AUTHORING (creating/editing a binding's config) stays admin-only — a member
  // can be granted VIEWER/EDITOR on a binding's DATA without being an org-admin
  // who can reconfigure it. The seeded demo binding has no real TableDef to
  // grant against, so it stays admin-only outright regardless of Tier-4.
  const { bypassesGroups, rbacResolving } = usePermissions();
  const canAuthorBindings = bypassesGroups;
  const [showBuilder, setShowBuilder] = useState(false);

  // Server-stored bindings: published bridge-type tables carry their Pairing
  // config in BridgeBindingJson. Surface them alongside the seeded set so
  // server-owned pairings appear in the picker.
  const [serverBindings, setServerBindings] = useState<BridgeDemoConfig[]>([]);
  // Starts true so a non-admin member isn't denied before the first list
  // resolves — until we know which server bindings exist, we can't know which
  // they're granted on (see the deny gate below).
  const [loadingServerBindings, setLoadingServerBindings] = useState(true);
  const reloadServerBindings = useCallback(() => {
    if (!token) return;
    setLoadingServerBindings(true);
    myappApi.tables
      .listAll(token, { isPublished: true })
      .then((items) => {
        setServerBindings(
          items
            .map(bridgeTableToConfig)
            .filter((c): c is BridgeDemoConfig => c !== null),
        );
      })
      .catch(() => {
        // Leave the previous list intact on a transient failure — wiping it
        // to [] here would make every server binding vanish from the picker
        // whenever this reload (fired after every save/delete AND on mount)
        // hits a blip, not just the one actually being deleted.
        toast.error("Failed to refresh the binding list.");
      })
      .finally(() => setLoadingServerBindings(false));
  }, [token]);
  useEffect(() => {
    reloadServerBindings();
  }, [reloadServerBindings]);

  const bindings = useMemo(
    () => [...BRIDGE_BINDINGS, ...serverBindings],
    [serverBindings],
  );

  // Tier-4: a non-admin member reaches Pairing only via an explicit per-table
  // grant on a server-stored binding (the bridge/junction table itself — the
  // same table_id the backend's bridge endpoints are role-gated on). Reuses the
  // shared batch-permissions hook; admins pass `[]` so it skips the round-trip
  // entirely and see every binding (unchanged behaviour — the hook's own
  // empty-ids guard both no-ops the fetch and leaves loading false).
  const { permissions: serverBindingPerms, loading: loadingBindingPerms } =
    useMyAppBatchPermissions(bypassesGroups ? [] : serverBindings.map((b) => b.bridgeTableId));

  // The picker only ever offers what this user can actually reach: admins see
  // everything (seeded + server + local, unchanged); a granted member sees
  // ONLY the server-stored bindings they have an explicit can_view grant on.
  const visibleBindings = useMemo(
    () =>
      bypassesGroups
        ? bindings
        : serverBindings.filter((b) => serverBindingPerms[b.bridgeTableId]?.can_view),
    [bypassesGroups, bindings, serverBindings, serverBindingPerms],
  );

  const [bindingId, setBindingId] = useState<string>(BRIDGE_BINDINGS[0]?.id ?? "");

  // A non-admin's default selection can't be the (admin-only) seeded binding —
  // once their visible set resolves, land on the first one they can actually
  // see. NEVER blanks bindingId to "": `bypassesGroups` defaults to `false`
  // while PortalDataProvider's RBAC fetch is still in flight (no separate
  // loading flag), so on that transient render `visibleBindings` is `[]` for
  // an ADMIN too — blanking here would permanently clobber the seeded default
  // before `bypassesGroups` has a chance to flip true. Only ever redirect to a
  // CONCRETE alternative; if there isn't one yet, do nothing and let the next
  // effect run (once permissions/bindings actually resolve) decide.
  useEffect(() => {
    if (bypassesGroups) return;
    if (visibleBindings.length === 0) return;
    if (visibleBindings.some((b) => b.id === bindingId)) return;
    setBindingId(visibleBindings[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the visible set itself changes, not on every bindingId write
  }, [bypassesGroups, visibleBindings]);
  // Derived from visibleBindings (not the raw `bindings`) so a stale/racy
  // bindingId can never resolve to a binding this user isn't granted on.
  const cfg = visibleBindings.find((b) => b.id === bindingId) ?? null;
  const [poId, setPoId] = useState<number | null>(null);
  // The picked PO record, held at page level so the picker can re-show its label
  // after a view-toggle remount (the combobox's internal selection dies with the
  // unmount, but poId/rowRecordIds — and therefore the matrix — survive it).
  const [poItem, setPoItem] = useState<Record<string, unknown> | null>(null);
  const [view, setView] = useState<"po" | "container" | "receiving">("po");
  // Set when the receiving view is opened via a realised column's "Receive →"
  // link on the matrix, so ReceivingView opens pre-selecting that container.
  const [receivingContainerId, setReceivingContainerId] = useState<number | null>(null);
  // A stale receivingContainerId is a bare record id scoped to the PREVIOUS
  // binding's container table — surviving a binding switch would pre-select
  // an unrelated (or nonexistent) container under the new binding. Clear it
  // whenever the selected binding changes.
  useEffect(() => {
    setReceivingContainerId(null);
  }, [bindingId]);
  const [rowRecordIds, setRowRecordIds] = useState<number[]>([]);
  const [unitVolumeByRow, setUnitVolumeByRow] = useState<Record<number, number>>({});
  // Composite mode only: gate state from the new filtered bridge/composite read,
  // consumed by AllocationMatrix's "· locked" marker (dim-A side). Flat mode
  // never sets these — gate-closed lines never reach the flat worklist.
  const [headerLocked, setHeaderLocked] = useState(false);
  const [lineEditableByRow, setLineEditableByRow] = useState<Record<number, boolean>>({});
  const [loadingLines, setLoadingLines] = useState(false);
  // Flat mode close-out: concluded lines are hidden from the worklist by default.
  const [showConcluded, setShowConcluded] = useState(false);
  // Two distinct failure surfaces: the PO *search* (combobox fetch) and the
  // selected PO's *lines* load — kept separate so a Retry never retries (or a
  // success never masks) the wrong operation.
  const [poSearchError, setPoSearchError] = useState<string | null>(null);
  const [posError, setPosError] = useState<string | null>(null);
  // Flat mode's worklist request is capped at 200 rows with no pagination —
  // surface it when the binding has more open/matching rows than that so a
  // truncated view is visible rather than silent (BUGS.md: "flat-mode
  // worklist hard-limits 200 rows with no pagination, silently truncating").
  const [flatTruncated, setFlatTruncated] = useState<{ shown: number; total: number } | null>(
    null,
  );

  // Build the dim-A unit-volume map from a set of records (shared composite/flat).
  const unitVolumeOf = useCallback(
    (records: Record<string, unknown>[]): Record<number, number> => {
      const uv: Record<number, number> = {};
      // No conversion column picked → treat unit-volume as 1:1 (factor 1). Without
      // this guard `record[""]` is undefined → NaN, silently dropping every entry.
      if (!cfg || !cfg.poLinesUnitVolumeColumn) return uv;
      for (const r of records) {
        const id = Number(r[cfg.poLinesPkColumn]);
        const v = Number(r[cfg.poLinesUnitVolumeColumn]);
        if (Number.isFinite(id) && Number.isFinite(v) && v > 0) uv[id] = v;
      }
      return uv;
    },
    [cfg],
  );

  // composite mode: the parent (PO) picker's search. Server-side `search` with a
  // completeness flag — small orgs get one fetch + instant local filtering
  // (SearchCombobox's enableLocalFilter), large ones fall through to
  // per-keystroke server search. Replaces the old eager `limit: 200` list that
  // made POs beyond 200 silently unreachable.
  // NB match-semantics asymmetry (deliberate): small tables are filtered locally
  // on the label column only, while large/truncated tables hit the backend's
  // all-text-columns search — a query matching e.g. a supplier column finds POs
  // only via the server path. Acceptable for a picker keyed by its label.
  // Close-out: when the binding configures a parent block, the picker is driven
  // by the bridge WORKLIST — only parents still open for pairing (gate-allowed,
  // not fully concluded) are offered, each with live progress. Bindings without
  // a parent block keep the raw records.list path (legacy behaviour).
  const hasParentWorklist = !!cfg?.binding.dim_a.parent?.link_field_id;

  const poSearchFn = useCallback(
    async (tok: string, query: string) => {
      if (!cfg?.poHeaderTableId) return { items: [] as Record<string, unknown>[] };
      try {
        if (cfg.binding.dim_a.parent?.link_field_id) {
          const res = await myappApi.bridge.worklist(tok, cfg.bridgeTableId, cfg.binding, {
            mode: "parents",
            search: query.trim() || undefined,
            limit: 100,
          });
          setPoSearchError(null);
          return {
            items: res.items as unknown as Record<string, unknown>[],
            isComplete: res.items.length >= res.total,
          };
        }
        const res = await myappApi.records.list(tok, cfg.poHeaderTableId, {
          search: query.trim() || undefined,
          limit: 100,
        });
        setPoSearchError(null);
        return {
          items: res.items as Record<string, unknown>[],
          isComplete: res.items.length >= res.total,
        };
      } catch (e) {
        setPoSearchError(e instanceof Error ? e.message : "Failed to load parent records");
        return { items: [] as Record<string, unknown>[] };
      }
    },
    [cfg],
  );

  // Progress subtitle for a worklist-driven parent option: "3/5 lines · 120 left".
  // Quantity-mode bindings (a dim-A quantity field + a units field) report
  // "left" in UNITS — label it, since the same figure used to be volume.
  const bindingRowsInUnits = !!(
    cfg?.binding?.dim_a?.quantity_field_id && cfg?.binding?.units_field_id
  );
  const worklistProgress = (r: Record<string, unknown>): string | null => {
    const parts: string[] = [];
    if (typeof r.lines_total === "number" && r.lines_total > 0) {
      parts.push(`${Number(r.lines_concluded ?? 0)}/${r.lines_total} lines`);
    }
    if (typeof r.remaining_sum === "number" && Number.isFinite(r.remaining_sum)) {
      parts.push(
        `${r.remaining_sum.toLocaleString()}${bindingRowsInUnits ? " units" : ""} left`,
      );
    }
    // Quantity-stage variance (dp #1147): target_sum already carries the
    // effective (actual-overriding-planned) figure; planned_sum is the plan.
    // Equal (or planned_sum absent) when no stage is configured/recorded.
    if (
      typeof r.target_sum === "number" &&
      typeof r.planned_sum === "number" &&
      Number.isFinite(r.target_sum) &&
      Number.isFinite(r.planned_sum) &&
      r.target_sum !== r.planned_sum
    ) {
      const variance = r.target_sum - r.planned_sum;
      // Signed to match the matrix badge's "Under −2" / "Over +3" convention.
      parts.push(variance < 0 ? `Under ${variance.toLocaleString()}` : `Over +${variance.toLocaleString()}`);
    }
    return parts.length ? parts.join(" · ") : null;
  };

  // flat mode: dim-A records ARE the rows — the close-out worklist lists the
  // OPEN lines (dim-A gate applied, concluded hidden unless toggled). The matrix
  // read supplies the authoritative unit_volume factor, so no unit-volume map is
  // needed here (the old records.list omitted calc columns anyway).
  const loadFlatRows = useCallback(() => {
    if (!token || !cfg) return;
    setLoadingLines(true);
    setPosError(null);
    // Cleared at request start (not just set on success) — otherwise a
    // reload that fails, or one whose new result isn't truncated, leaves the
    // PREVIOUS request's "Showing N of M" numbers rendered next to the error.
    setFlatTruncated(null);
    myappApi.bridge
      .worklist(token, cfg.bridgeTableId, cfg.binding, {
        mode: "lines",
        limit: 200,
        includeConcluded: showConcluded,
      })
      .then((res) => {
        setRowRecordIds(res.items.map((i) => i.record_id));
        setUnitVolumeByRow({});
        setFlatTruncated(
          res.items.length < res.total ? { shown: res.items.length, total: res.total } : null,
        );
      })
      .catch((e) => setPosError(e instanceof Error ? e.message : "Failed to load rows"))
      .finally(() => setLoadingLines(false));
  }, [token, cfg, showConcluded]);

  // flat mode loads its rows eagerly; composite mode's PO combobox lazy-loads
  // on open/type, so there is nothing to prefetch.
  useEffect(() => {
    if (cfg?.rowMode === "flat") loadFlatRows();
  }, [cfg, loadFlatRows]);

  // composite mode: load the selected parent's line ids (dimension-A records).
  const loadLines = useCallback(
    async (selectedPoId: number) => {
      if (!token || !cfg || !cfg.poHeaderTableId) return;
      setLoadingLines(true);
      setPosError(null);
      try {
        // Always passes cfg.binding: for a server-stored bridge-type table
        // the backend's `_effective_binding` treats the stored config as
        // authoritative and ignores it; for the seeded demo binding it's the
        // only source the endpoint has.
        const res = await myappApi.bridge.readComposite(
          token,
          cfg.bridgeTableId,
          cfg.binding,
          selectedPoId,
        );
        setRowRecordIds(res.lines.map((l) => l.record_id));
        // Per-line unit-volume → lets the matrix show units (entry) while the
        // other dimension reconciles by the measure.
        setUnitVolumeByRow(
          unitVolumeOf(res.lines.map((l) => ({ ...l.data, [cfg.poLinesPkColumn]: l.record_id }))),
        );
        setHeaderLocked(!res.header.gate.open);
        setLineEditableByRow(Object.fromEntries(res.lines.map((l) => [l.record_id, l.editable])));
      } catch (e) {
        setPosError(e instanceof Error ? e.message : "Failed to load the parent's lines");
      } finally {
        setLoadingLines(false);
      }
    },
    [token, cfg, unitVolumeOf],
  );

  // Switching binding resets the parent selection + derived rows.
  const onBindingChange = (id: string) => {
    setBindingId(id);
    setPoId(null);
    setPoItem(null);
    setRowRecordIds([]);
    setUnitVolumeByRow({});
    setHeaderLocked(false);
    setLineEditableByRow({});
    setShowConcluded(false);
    setPoSearchError(null);
    setPosError(null);
    setFlatTruncated(null);
  };

  // Builder saved a binding (new or edited) → refresh the server list, select
  // it, close.
  const onBuilderSaved = (saved: BridgeDemoConfig) => {
    // Optimistically surface the just-saved binding so it's selectable on this
    // render; reloadServerBindings() then reconciles with the server list.
    setServerBindings((prev) => [...prev.filter((b) => b.id !== saved.id), saved]);
    reloadServerBindings();
    setShowBuilder(false);
    onBindingChange(saved.id);
  };

  // EDIT a server binding in place: reopen the builder pre-filled from the
  // stored config (labels/targets/gates freely editable; the structural tier
  // locks once the pairing has data — BE-mirrored, see BindingBuilder).
  const [editingServer, setEditingServer] = useState<{
    form: FormDetail;
    config: BridgeDemoConfig;
    hasData: boolean;
  } | null>(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const onEditServerBinding = async (config: BridgeDemoConfig) => {
    if (!token) return;
    setLoadingEdit(true);
    try {
      const form = await myappApi.tables.get(token, config.bridgeTableId);
      // Data probe for the structural lock — includeDeleted/Archived so the
      // FE lock matches the BE guard, which counts ANY physical row (cleared
      // and soft-deleted rows are audit history keyed by the current binding).
      let hasData = false;
      try {
        const page = await myappApi.records.list(token, config.bridgeTableId, {
          limit: 1,
          includeDeleted: true,
          includeArchived: true,
        });
        hasData = (page.total ?? page.items?.length ?? 0) > 0;
      } catch {
        hasData = true; // unknown → assume data (lock rather than under-guard)
      }
      setEditingServer({ form, config, hasData });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load the binding for editing.");
    } finally {
      setLoadingEdit(false);
    }
  };

  // Delete a server binding — this HARD-deletes the underlying bridge-type
  // table (the binding IS that table): the physical SQL table is dropped and
  // the TableDef row removed, so it leaves the picker for good and cannot be
  // recovered. The seeded demo binding never reaches here — the delete
  // button only renders when `activeIsServer`.
  const [deletingServer, setDeletingServer] = useState(false);
  const onDeleteServerBinding = async (bindingIdToDelete: string, bridgeTableId: string) => {
    if (!token) return;
    setDeletingServer(true);
    try {
      // The backend requires type-to-confirm (exact table name) plus an
      // explicit data-loss flag for published tables — `tables.delete` sends
      // neither and always 422s, so fetch the table's real name first and
      // go through `deleteWithConfirm`. Pairing's own window.confirm() above
      // already gets explicit user intent, so confirm_data_loss is implied.
      const form = await myappApi.tables.get(token, bridgeTableId);
      await myappApi.tables.deleteWithConfirm(token, bridgeTableId, form.name, true);
      const remainingServer = serverBindings.filter((b) => b.id !== bindingIdToDelete);
      setServerBindings(remainingServer);
      // Re-select a still-present binding (seeded → remaining server) rather
      // than dropping to an empty picker when there are no seeded ones.
      if (bindingId === bindingIdToDelete) {
        onBindingChange(BRIDGE_BINDINGS[0]?.id ?? remainingServer[0]?.id ?? "");
      }
      reloadServerBindings();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete the binding.");
    } finally {
      setDeletingServer(false);
    }
  };

  const activeIsServer = cfg?.source === "server";

  // "In preview" notice — shown to everyone with access, on every render path.
  const previewBanner = (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-2 text-sm text-sky-800">
      <span className="rounded-full bg-sky-600 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
        In preview
      </span>
      <span>Pairing is an early-access feature — behaviour and layout may still change.</span>
    </div>
  );

  // Organisation owners/admins + platform admins (bypassesGroups) always reach
  // the page. A non-admin member reaches it too, but ONLY once they have an
  // explicit can_view grant on at least one server-stored binding. Hold the
  // deny screen while ANY of the three inputs is still resolving, so neither a
  // genuine admin nor a granted member ever flashes a false "denied":
  //   - the backend RBAC answer (`bypassesGroups` defaults to false until the
  //     permissions round-trip settles — `rbacResolving`),
  //   - the server-binding list (which bindings exist at all), and
  //   - the per-binding grant fetch (which of them this member can view).
  if (!bypassesGroups && (rbacResolving || loadingServerBindings || loadingBindingPerms)) {
    return (
      <div className="space-y-6">
        <Helmet>
          <title>Pairing · MyApp</title>
        </Helmet>
        <PageHeader title="Pairing" description="Pair records across two dimensions and reconcile against a target." />
      </div>
    );
  }
  if (!bypassesGroups && visibleBindings.length === 0) {
    return (
      <div className="space-y-6">
        <Helmet>
          <title>Pairing · MyApp</title>
        </Helmet>
        <PageHeader title="Pairing" description="Pair records across two dimensions and reconcile against a target." />
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-muted-foreground">
          You don't have access to any pairings yet. Ask your organisation admin to grant you
          a role on one.
        </div>
      </div>
    );
  }

  if (!cfg) {
    return (
      <div className="space-y-6">
        <Helmet>
          <title>Pairing · MyApp</title>
        </Helmet>
        <PageHeader title="Pairing" description="Pair records across two dimensions and reconcile against a target." />
        {previewBanner}
        {showBuilder && canAuthorBindings ? (
          <BindingBuilder
            onSaved={onBuilderSaved}
            onCancel={() => setShowBuilder(false)}
            boundTableIds={bindings.map((b) => b.bridgeTableId)}
          />
        ) : canAuthorBindings ? (
          <div className="space-y-4 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
            <p>
              No reconciliation bindings are configured yet. Build one with the binding builder, or
              add a seeded entry to <code>BRIDGE_BINDINGS</code> in{" "}
              <code>routes/portal/myapp/bridge.tsx</code>.
            </p>
            <Button type="button" size="sm" onClick={() => setShowBuilder(true)}>
              New binding
            </Button>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-muted-foreground">
            No pairings are configured for this organisation yet — ask Larasoft to set one up.
          </div>
        )}
      </div>
    );
  }

  const isFlat = cfg.rowMode === "flat";
  const parentLabel = cfg.rowParentLabel ?? "Purchase order";
  // In flat mode there's no parent picker, so the "By X" toggle reads generically.
  const rowsLabel = isFlat ? "Rows" : parentLabel;
  // Receiving (dp #1581 / plan myapp-pairing-receiving-workflow): only show
  // the tab once the binding actually carries the receiving roles — legacy
  // bindings (no realised_values / no junction receiving fields) render
  // exactly as before, no new UI surface.
  const receivingConfigured =
    (cfg.binding.dim_b.realised_values?.length ?? 0) > 0 &&
    !!cfg.binding.actual_units_field_id &&
    !!cfg.binding.variance_reason_field_id &&
    !!cfg.binding.resolution_field_id;

  return (
    <div className="space-y-6">
      <Helmet>
        <title>{cfg.label} · MyApp</title>
      </Helmet>
      <PageHeader
        title={cfg.label}
        description={cfg.description ?? "Pair records across two dimensions and reconcile against a target."}
      />

      {previewBanner}

      {/* Controls card (house style — mirrors the Tables page toolbar). */}
      <div className="space-y-4 rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          {visibleBindings.length > 1 && (
            <>
              <label className="text-sm font-medium">Binding</label>
              <Combobox
                options={visibleBindings.map((b) => ({
                  value: b.id,
                  label: `${b.label}${b.source === "server" ? " (server)" : ""}`,
                }))}
                value={bindingId}
                onValueChange={(v) => {
                  if (v) onBindingChange(v);
                }}
                ariaLabel="Binding"
                placeholder="Select a binding…"
                className="w-full min-w-[260px] flex-1 sm:max-w-md"
              />
            </>
          )}
          {canAuthorBindings && (
            <Button type="button" variant="outline" size="sm" onClick={() => setShowBuilder((s) => !s)}>
              {showBuilder ? "Hide builder" : "New binding"}
            </Button>
          )}
          {canAuthorBindings && activeIsServer && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loadingEdit || !!editingServer}
              onClick={() => void onEditServerBinding(cfg)}
            >
              {loadingEdit ? "Loading…" : "Edit this binding"}
            </Button>
          )}
          {canAuthorBindings && activeIsServer && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-rose-600 hover:text-rose-700"
              disabled={deletingServer}
              onClick={() => {
                if (
                  window.confirm(
                    `Delete the binding "${cfg.label}"? This removes its bridge table AND all its pairing data. To change labels, targets or gates use "Edit this binding" instead.`,
                  )
                )
                  void onDeleteServerBinding(cfg.id, cfg.bridgeTableId);
              }}
            >
              {deletingServer ? "Deleting…" : "Delete this binding"}
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-sm">
            <button
              type="button"
              onClick={() => setView("po")}
              className={`rounded-md px-3 py-1 ${view === "po" ? "bg-slate-900 text-white" : "text-slate-600"}`}
            >
              By {rowsLabel}
            </button>
            <button
              type="button"
              onClick={() => setView("container")}
              className={`rounded-md px-3 py-1 ${view === "container" ? "bg-slate-900 text-white" : "text-slate-600"}`}
            >
              By Container
            </button>
            {receivingConfigured && (
              <button
                type="button"
                onClick={() => setView("receiving")}
                className={`rounded-md px-3 py-1 ${view === "receiving" ? "bg-slate-900 text-white" : "text-slate-600"}`}
              >
                Receiving
              </button>
            )}
          </div>

          {view === "po" && isFlat && (
            <label className="flex items-center gap-1.5 text-sm sm:ml-2">
              <input
                type="checkbox"
                checked={showConcluded}
                onChange={(e) => setShowConcluded(e.target.checked)}
              />
              Show concluded lines
            </label>
          )}

          {view === "po" && !isFlat && (
            <>
              <label className="text-sm font-medium sm:ml-2">{parentLabel}</label>
              <SearchCombobox
                key={cfg.id}
                token={token}
                value={poId != null ? String(poId) : ""}
                initialItem={poItem}
                onValueChange={(id, item) => {
                  const n = Number(id);
                  if (!Number.isFinite(n)) return;
                  setPoId(n);
                  setPoItem(item);
                  setRowRecordIds([]);
                  void loadLines(n);
                }}
                searchFn={poSearchFn}
                enableLocalFilter
                getId={(r) =>
                  hasParentWorklist
                    ? String(r.record_id ?? "")
                    : String(cfg.poHeaderPkColumn ? r[cfg.poHeaderPkColumn] : "")
                }
                getLabel={(r) => {
                  if (hasParentWorklist) {
                    return String(r.label ?? `#${String(r.record_id ?? "?")}`);
                  }
                  const lbl = cfg.poHeaderLabelColumn ? r[cfg.poHeaderLabelColumn] : undefined;
                  return String(lbl ?? `#${cfg.poHeaderPkColumn ? r[cfg.poHeaderPkColumn] : "?"}`);
                }}
                getSecondaryLabel={hasParentWorklist ? worklistProgress : undefined}
                optionIdPrefix="bridge-po-option"
                placeholder={`Select a ${parentLabel.toLowerCase()}…`}
                searchPlaceholder={`Search ${parentLabel.toLowerCase()}s…`}
                emptyMessage={`No ${parentLabel.toLowerCase()}s found.`}
                ariaLabel={parentLabel}
                className="w-72"
              />
              {loadingLines && <span className="text-xs text-muted-foreground">Loading lines…</span>}
              {poSearchError && (
                <span className="text-xs text-rose-600">
                  {poSearchError} — reopen the picker to retry.
                </span>
              )}
              {posError && (
                <span className="text-xs text-rose-600">
                  {posError}
                  {poId != null && (
                    <>
                      {" "}
                      <button type="button" className="underline" onClick={() => void loadLines(poId)}>
                        Retry
                      </button>
                    </>
                  )}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {showBuilder && canAuthorBindings && (
        <BindingBuilder
          onSaved={onBuilderSaved}
          onCancel={() => setShowBuilder(false)}
          boundTableIds={bindings.map((b) => b.bridgeTableId)}
        />
      )}

      {/* Edit the selected server binding in place — pre-filled builder; the
          structural tier locks when the pairing already has data. */}
      {editingServer && canAuthorBindings && (
        <BindingBuilder
          lockedTable={editingServer.form}
          initialConfig={editingServer.config}
          hasAllocations={editingServer.hasData}
          onSaved={(saved) => {
            setEditingServer(null);
            onBuilderSaved(saved);
          }}
          onCancel={() => setEditingServer(null)}
        />
      )}

      {/* Read-only view of the selected binding's field→role mapping. Only for
          server-stored bindings (a real bridge table whose fields resolve);
          the seeded demo binding has no live table to resolve names against. */}
      {cfg.source === "server" && (
        <details className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <summary className="cursor-pointer text-sm font-semibold text-slate-700">
            Binding configuration (read-only)
          </summary>
          <div className="mt-4">
            <BindingSummaryCard config={cfg} />
          </div>
        </details>
      )}

      {view === "container" ? (
        <ContainerPackingView key={cfg.id} cfg={cfg} />
      ) : view === "receiving" ? (
        <ReceivingView
          key={`${cfg.id}:${receivingContainerId ?? ""}`}
          cfg={cfg}
          initialContainerId={receivingContainerId}
        />
      ) : (
        <>
          {isFlat && loadingLines && (
            <span className="text-xs text-muted-foreground">Loading rows…</span>
          )}
          {isFlat && !loadingLines && flatTruncated && (
            <span className="text-xs text-amber-600" role="status" aria-live="polite">
              Showing {flatTruncated.shown} of {flatTruncated.total} rows — rows beyond the first{" "}
              {flatTruncated.shown} are not visible.
            </span>
          )}
          {isFlat && posError && (
            <span className="text-xs text-rose-600">
              {posError}{" "}
              <button type="button" className="underline" onClick={loadFlatRows}>
                Retry
              </button>
            </span>
          )}

          <AllocationMatrix
            key={cfg.id}
            bridgeTableId={cfg.bridgeTableId}
            binding={cfg.binding}
            rowRecordIds={rowRecordIds}
            unitVolumeByRow={unitVolumeByRow}
            headerLocked={isFlat ? undefined : headerLocked}
            lineEditableByRow={isFlat ? undefined : lineEditableByRow}
            // Flat mode: this page's own "Show concluded lines" checkbox
            // already drives the refetch that surfaces concluded rows — pass
            // it straight through so the matrix doesn't ALSO need its own
            // toggle clicked to actually display them (the two-controls bug).
            // Composite mode: leave uncontrolled — every line is loaded
            // upfront, so the matrix's self-contained toggle is sufficient.
            showConcluded={isFlat ? showConcluded : undefined}
            onOpenReceiving={
              receivingConfigured
                ? (colId) => {
                    setReceivingContainerId(colId);
                    setView("receiving");
                  }
                : undefined
            }
          />
        </>
      )}
    </div>
  );
}
